import { FC, JSX, useContext, useMemo, useState } from "react";
import { ColumnConfig, SortDirections, WafflesPaginationProps } from "@app/shared/waffles";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import { sortProviderNetworkTypes } from "@app/shared/enums/providerNetworkTypeEnum";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { wafflesKeyColumnName, WafflesThemes } from "@app/shared/waffles/exports";
import { ProviderBadgeStyle } from "@app/projects/enumerators";
import WaffleRowTooltip from "@app/shared/waffles/WaffleRowTooltip";
import PageSection from "@app/shared/PageSection";
import { Waffles } from "@app/shared/waffles/Waffles";
import { GapImpactPaginationResult, ProviderHubContractEntityProviders, ProviderHubContractEntityProvidersTableData, ProviderHubHospitalProviderSortColumn } from "@app/projects/network-assessment/provider-hub/interfaces";
import { useProviderLink } from "@app/projects/network-assessment/provider-hub/hooks/useProviderLink";
import { useContractEntityLink } from "@app/projects/network-assessment/provider-hub/hooks/useContractEntityLink";
import { useOrganizationLink } from "@app/projects/network-assessment/provider-hub/hooks/useOrganizationLink";
import { ProviderScoreColumnHeader } from "@app/projects/network-assessment/provider-hub/ProviderScoreColumnHeader";
import { NO_ACTIONS_APPLIED_LABEL, formatCountiesImpacted, formatGapImpactCount, formatScore, formatSpecialtyWithCode } from "@app/projects/network-assessment/provider-hub/utility";

// How the IAP drawer groups its providers: a flat list ("provider"), or expandable parent rows grouped by the
// provider's primary contract entity or organization.
export type HospitalProviderGroupBy = "provider" | "contractEntity" | "organization";

interface HospitalProfileTableProps {
    hospitalProviderTableData: GapImpactPaginationResult<ProviderHubContractEntityProviders>;
    groupBy?: HospitalProviderGroupBy;
    pageIndex: number;
    pageSize: number;
    resetToFirstPage: boolean;
    setHospitalProviderTableData?: React.Dispatch<React.SetStateAction<GapImpactPaginationResult<ProviderHubContractEntityProviders>>>;
    setPageIndex: React.Dispatch<React.SetStateAction<number>>;
    setPageSize: React.Dispatch<React.SetStateAction<number>>;
    setResetToFirstPage: React.Dispatch<React.SetStateAction<boolean>>;
    setSortColumnAndOrder: React.Dispatch<React.SetStateAction<ProviderHubHospitalProviderSortColumn>>;
}

const HospitalProfileTable: FC<React.PropsWithChildren<HospitalProfileTableProps>> = (props: HospitalProfileTableProps) => {
    const projectContext = useContext(CountyProjectContext);
    const providerLink = useProviderLink();
    const contractEntityLink = useContractEntityLink();
    const organizationLink = useOrganizationLink();

    const showClaims = projectContext.project.showPciData !== false;
    const showMotive = projectContext.project.showMotiveAppropriatenessScore;
    const showCareJourney = projectContext.project.showCareJourneyScores;

    const groupBy: HospitalProviderGroupBy = props.groupBy ?? "provider";
    const isGrouped = groupBy !== "provider";

    const [sortedColumn, setSortedColumn] = useState<keyof ProviderHubContractEntityProvidersTableData>("nameElement");
    const [sortDirection, setSortDirection] = useState<SortDirections>(SortDirections.Asc);

    /* oxlint-disable sort-keys */
    const columnSortMap: Record<string, { asc: ProviderHubHospitalProviderSortColumn; desc: ProviderHubHospitalProviderSortColumn }> = {
        nameElement: { asc: ProviderHubHospitalProviderSortColumn.NameAscending, desc: ProviderHubHospitalProviderSortColumn.NameDescending },
        identifier: { asc: ProviderHubHospitalProviderSortColumn.NpiAscending, desc: ProviderHubHospitalProviderSortColumn.NpiDescending },
        contractEntity: { asc: ProviderHubHospitalProviderSortColumn.ContractEntityAscending, desc: ProviderHubHospitalProviderSortColumn.ContractEntityDescending },
        organization: { asc: ProviderHubHospitalProviderSortColumn.OrganizationAscending, desc: ProviderHubHospitalProviderSortColumn.OrganizationDescending },
        affiliationStrength: { asc: ProviderHubHospitalProviderSortColumn.AffiliationStrengthAscending, desc: ProviderHubHospitalProviderSortColumn.AffiliationStrengthDescending },
        gapImpact: { asc: ProviderHubHospitalProviderSortColumn.GapsClosedAscending, desc: ProviderHubHospitalProviderSortColumn.GapsClosedDescending },
        activeGapImpact: { asc: ProviderHubHospitalProviderSortColumn.ActiveGapsClosedAscending, desc: ProviderHubHospitalProviderSortColumn.ActiveGapsClosedDescending },
        pendingGapImpact: { asc: ProviderHubHospitalProviderSortColumn.PendingGapsClosedAscending, desc: ProviderHubHospitalProviderSortColumn.PendingGapsClosedDescending },
        approvedGapImpact: { asc: ProviderHubHospitalProviderSortColumn.ApprovedGapsClosedAscending, desc: ProviderHubHospitalProviderSortColumn.ApprovedGapsClosedDescending },
        opportunityGapImpact: { asc: ProviderHubHospitalProviderSortColumn.OpportunityGapsClosedAscending, desc: ProviderHubHospitalProviderSortColumn.OpportunityGapsClosedDescending },
        filteredQuality: { asc: ProviderHubHospitalProviderSortColumn.EffectivenessAscending, desc: ProviderHubHospitalProviderSortColumn.EffectivenessDescending },
        filteredCost: { asc: ProviderHubHospitalProviderSortColumn.EfficiencyAscending, desc: ProviderHubHospitalProviderSortColumn.EfficiencyDescending },
        totalClaims: { asc: ProviderHubHospitalProviderSortColumn.TotalClaimsAscending, desc: ProviderHubHospitalProviderSortColumn.TotalClaimsDescending },
        motiveAppropriateness: { asc: ProviderHubHospitalProviderSortColumn.MotiveAscending, desc: ProviderHubHospitalProviderSortColumn.MotiveDescending },
        careJourneyCost: { asc: ProviderHubHospitalProviderSortColumn.CareJourneyCostAscending, desc: ProviderHubHospitalProviderSortColumn.CareJourneyCostDescending },
        careJourneyQuality: { asc: ProviderHubHospitalProviderSortColumn.CareJourneyQualityAscending, desc: ProviderHubHospitalProviderSortColumn.CareJourneyQualityDescending }
    };
    /* oxlint-enable sort-keys */

    // Backend sort: Waffles reports the clicked column/direction; we translate to the sort enum and refetch (passThroughSorting).
    const handleSortChange = (records: ProviderHubContractEntityProvidersTableData[], column: keyof ProviderHubContractEntityProvidersTableData, direction: SortDirections): ProviderHubContractEntityProvidersTableData[] => {
        const mapping = columnSortMap[column as string];
        if (mapping && direction !== SortDirections.None) {
            setSortedColumn(column);
            setSortDirection(direction);
            props.setSortColumnAndOrder(direction === SortDirections.Desc ? mapping.desc : mapping.asc);
            props.setPageIndex(1);
        }
        return records;
    };

    const useRegionStatusBreakdown = useMemo<boolean>(() => {
        return props.hospitalProviderTableData.useGapsByRegionStatus === true;
    }, [props.hospitalProviderTableData]);

    /* oxlint-disable sort-keys */
    const providerHubProvidersTableColumnConfig: ColumnConfig = {
        nameElement: {
            displayText: "Name",
            sizeContentToFit: true,
            sticky: true,
            testId: "provider-hub_hospital-table-provider-name"
        },
        identifier: {
            displayText: "Identifier",
            sizeContentToFit: true,
            sticky: true,
            testId: "provider-hub_hospital-table-identifier"
        },
        providerNetworkTypes: {
            displayText: "Provider Source",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-provider-source",
            tooltip: "Indicates the original database from which the provider data was obtained",
            noSort: true
        },
        providerActionStatuses: {
            displayText: "Status",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-status",
            tooltip: "Indicates any actions that have been applied to either a specific access point or across access points for a provider",
            noSort: true
        },
        affiliationStrength: {
            displayText: "Hospital Affil. Strength",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-affiliationstrength",
            tooltip: "The percentage of a provider’s institutional claims that are submitted through the selected hospital."
        },
        contractEntity: {
            displayText: "Contract Entity",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-contract-entity",
            tooltip: "The provider's primary contract entity",
            // Sortable by CE name in the flat view; grouped views sort client-side, so disable header sort there.
            noSort: isGrouped,
            // When grouping by contract entity, the group is the expandable parent header, so drop the redundant column.
            hidden: groupBy === "contractEntity"
        },
        organization: {
            displayText: "Organization",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-organization",
            tooltip: "The provider's primary organization",
            // Sortable by organization name in the flat view; grouped views sort client-side, so disable header sort there.
            noSort: isGrouped,
            // When grouping by organization, the group is the expandable parent header, so drop the redundant column.
            hidden: groupBy === "organization"
        },
        gapImpact: {
            displayText: "Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-gap-impact",
            tooltip: "Indicates the distinct number of gaps that the provider closes",
            hidden: useRegionStatusBreakdown
        },
        activeGapImpact: {
            displayText: "Active Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-active-gap-impact",
            tooltip: "Count of gaps closeable in Active service area counties",
            hidden: !useRegionStatusBreakdown
        },
        pendingGapImpact: {
            displayText: "Pending Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-pending-gap-impact",
            tooltip: "Count of gaps closeable in Pending service area counties",
            hidden: !useRegionStatusBreakdown
        },
        approvedGapImpact: {
            displayText: "Approved Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-approved-gap-impact",
            tooltip: "Count of gaps closeable in Approved service area counties",
            hidden: !useRegionStatusBreakdown
        },
        opportunityGapImpact: {
            displayText: "Opportunity Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-opportunity-gap-impact",
            tooltip: "Count of gaps closeable in Opportunity-to-expand service area counties",
            hidden: !useRegionStatusBreakdown
        },
        specialtiesImpacted: {
            displayText: "Specialty Groups Impacted",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-specialty",
            noSort: true
        },
        countiesImpacted: {
            displayText: "Counties Impacted",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-county",
            noSort: true
        },
        accessPoints: {
            displayText: "Access Points",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-filtered-oonsp",
            noSort: true
        },
        filteredQuality: {
            displayText: "Effectiveness (Quality)",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-filtered-quality",
            hidden: !showClaims
        },
        filteredCost: {
            displayText: "Efficiency (Cost)",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-filtered-cost",
            hidden: !showClaims
        },
        motiveAppropriateness: {
            displayElement: <ProviderScoreColumnHeader variant="motive" />,
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-motive-appropriateness",
            hidden: !showMotive
        },
        careJourneyCost: {
            displayText: "CareJourney Cost",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-carejourney-cost",
            hidden: !showCareJourney
        },
        careJourneyQuality: {
            displayText: "CareJourney Quality",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-carejourney-quality",
            hidden: !showCareJourney
        },
        totalClaims: {
            displayText: "Total Claims",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-filteredclaims",
            hidden: !showClaims
        },
        medicareClaims: {
            displayText: "Medicare Claims",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-medicareclaims",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        medicaidClaims: {
            displayText: "Medicaid Claims",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-medicaidclaims",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        commercialClaims: {
            displayText: "Commercial Claims",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-commercialclaims",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        vaClaims: {
            displayText: "VA Claims",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-vaclaims",
            hidden: projectContext.project.showVaClaimsVolumeColumn === false,
            noSort: true
        },
        tricareClaims: {
            displayText: "TRICARE Claims",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-table-tricareclaims",
            hidden: projectContext.project.showTricareClaimsVolumeColumn === false,
            noSort: true
        }
    }
    /* oxlint-enable sort-keys */

    const handlePageSizeChange = (pageSize: number) => {
        props.setPageSize(pageSize);
        props.setPageIndex(1);
    }

    const handlePageChange = async (newPage: number) => {
        if (!props.resetToFirstPage) {
            props.setPageIndex(newPage);
        }
        if (props.resetToFirstPage) {
            props.setResetToFirstPage(false);
        }
    };

    /* oxlint-disable sort-keys */
    const buildProviderRow = (provider: ProviderHubContractEntityProviders): ProviderHubContractEntityProvidersTableData => {
            // `as` cast: the row carries a few extra cells (gapImpact / filteredStatuses) not declared on the row type.
            return {
                key: provider.servicingProviderKey,
                nameElement: <a
                    href={providerLink(provider.servicingProviderKey?.toLowerCase())}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`provider-hub-provider-view__provider--${provider.name}`}
                    className="demibold"
                >
                    {provider.name ?? "---"}
                </a>,
                name: provider.name ?? "---",
                identifier: provider.npi,
                providerNetworkTypes: (
                    <>
                        {sortProviderNetworkTypes(provider.providerNetworkTypes).map((networkType) =>
                            <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} key={networkType} providerNetworkType={networkType} />
                        )}
                    </>
                ),
                providerActionStatuses: provider.providerActionStatuses.length > 0 ? (
                    <>
                        {provider.providerActionStatuses.map((status, index) =>
                            <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot} key={index} status={status} />
                        )}
                    </>
                ) : (
                    <>{NO_ACTIONS_APPLIED_LABEL}</>
                ),
                filteredStatuses: provider.providerActionStatuses.map(status => status),
                affiliationStrength: Math.round((provider.affiliationStrength ?? 0) * 100),
                contractEntity: provider.professionalContractEntityName && provider.professionalContractEntityId
                    ? <a
                        href={contractEntityLink(provider.professionalContractEntityId)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`provider-hub-hospital-table__contract-entity--${provider.servicingProviderKey}`}
                        className="demibold"
                    >
                        {provider.professionalContractEntityName}
                    </a>
                    : "---",
                organization: provider.professionalOrganizationName && provider.professionalOrganizationNpi && provider.professionalOrganizationContractEntityId
                    ? <a
                        href={organizationLink(provider.professionalOrganizationNpi, provider.professionalOrganizationContractEntityId)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`provider-hub-hospital-table__organization--${provider.servicingProviderKey}`}
                        className="demibold"
                    >
                        {provider.professionalOrganizationName}
                    </a>
                    : "---",
                gapImpact: formatGapImpactCount(provider.gapsClosed),
                activeGapImpact: formatGapImpactCount(provider.activeGapsClosed),
                pendingGapImpact: formatGapImpactCount(provider.pendingGapsClosed),
                approvedGapImpact: formatGapImpactCount(provider.approvedGapsClosed),
                opportunityGapImpact: formatGapImpactCount(provider.opportunityGapsClosed),
                specialtiesImpacted: <WaffleRowTooltip
                    containerClassName="provider-hub-waffle-tooltip"
                    elements={provider.specialtiesImpacted?.map(formatSpecialtyWithCode)}
                    numberOfElementsPerRow={3} />,
                countiesImpacted: <WaffleRowTooltip
                    containerClassName="provider-hub-waffle-tooltip"
                    elements={formatCountiesImpacted(provider.countiesImpacted)}
                    numberOfElementsPerRow={3} />,
                accessPoints: provider.accessPointCount,
                filteredQuality: formatScore(provider.effectivenessRank),
                filteredCost: formatScore(provider.efficiencyRank),
                motiveAppropriateness: formatScore(provider.motiveAppropriateness),
                careJourneyCost: formatScore(provider.careJourneyCost),
                careJourneyQuality: formatScore(provider.careJourneyQuality),
                totalClaims: provider.totalClaimVolume ?? '---',
                medicareClaims: provider.medicareClaimVolume ?? '---',
                medicaidClaims: provider.medicaidClaimVolume ?? '---',
                commercialClaims: provider.commercialClaimVolume ?? '---',
                vaClaims: provider.vaClaimVolume ?? '---',
                tricareClaims: provider.tricareClaimVolume ?? '---'
            } as ProviderHubContractEntityProvidersTableData;
    };

    // Flat "Provider" view: one row per provider (server-paginated).
    const flatRows = useMemo<ProviderHubContractEntityProvidersTableData[]>(
        () => props.hospitalProviderTableData.items.map(buildProviderRow),
        [props.hospitalProviderTableData]
    );

    // A grouped parent row. Waffles derives which columns render from the keys present on the first data row
    // (Object.keys(data[0])), so a parent must carry the SAME key shape (and order) as a provider row — otherwise
    // the grouped table collapses to just the Name column. Non-name cells are left blank; the children carry the data.
    const buildGroupParentRow = (key: string, nameElement: JSX.Element | string, label: string): ProviderHubContractEntityProvidersTableData => {
        /* oxlint-disable sort-keys */
        return {
            key,
            nameElement,
            name: label,
            identifier: "",
            providerNetworkTypes: "",
            providerActionStatuses: "",
            filteredStatuses: [],
            affiliationStrength: "",
            contractEntity: "",
            organization: "",
            gapImpact: "",
            activeGapImpact: "",
            pendingGapImpact: "",
            approvedGapImpact: "",
            opportunityGapImpact: "",
            specialtiesImpacted: "",
            countiesImpacted: "",
            accessPoints: "",
            filteredQuality: "",
            filteredCost: "",
            motiveAppropriateness: "",
            careJourneyCost: "",
            careJourneyQuality: "",
            totalClaims: "",
            medicareClaims: "",
            medicaidClaims: "",
            commercialClaims: "",
            vaClaims: "",
            tricareClaims: ""
        } as unknown as ProviderHubContractEntityProvidersTableData;
        /* oxlint-enable sort-keys */
    };

    // Grouped views: one expandable parent row per contract entity / organization, with its providers as children.
    // The grouped column is hidden (see column config); the parent's Name cell links to that CE/Org profile.
    const { parentRows, expansions } = useMemo(() => {
        const parents: ProviderHubContractEntityProvidersTableData[] = [];
        const expansionMap = new Map<string, ProviderHubContractEntityProvidersTableData[]>();
        if (!isGrouped) return { expansions: expansionMap, parentRows: parents };

        const groups = new Map<string, { link: JSX.Element | string; label: string; children: ProviderHubContractEntityProvidersTableData[] }>();
        props.hospitalProviderTableData.items.forEach((provider) => {
            const groupId = groupBy === "contractEntity"
                ? (provider.professionalContractEntityId != null ? String(provider.professionalContractEntityId) : "")
                : (provider.professionalOrganizationNpi ?? "");
            const label = (groupBy === "contractEntity" ? provider.professionalContractEntityName : provider.professionalOrganizationName) ?? "---";
            const key = `group:${groupBy}:${groupId || "none"}`;

            if (!groups.has(key)) {
                const link: JSX.Element | string = groupBy === "contractEntity"
                    ? (provider.professionalContractEntityName && provider.professionalContractEntityId
                        ? <a href={contractEntityLink(provider.professionalContractEntityId)} target="_blank" rel="noreferrer" className="demibold" data-testid={`hospital-iap-drawer__group--${key}`}>{provider.professionalContractEntityName}</a>
                        : label)
                    : (provider.professionalOrganizationName && provider.professionalOrganizationNpi && provider.professionalOrganizationContractEntityId
                        ? <a href={organizationLink(provider.professionalOrganizationNpi, provider.professionalOrganizationContractEntityId)} target="_blank" rel="noreferrer" className="demibold" data-testid={`hospital-iap-drawer__group--${key}`}>{provider.professionalOrganizationName}</a>
                        : label);
                groups.set(key, { children: [], label, link });
            }
            groups.get(key)!.children.push(buildProviderRow(provider));
        });

        for (const [key, group] of groups) {
            parents.push(buildGroupParentRow(key, group.link, group.label));
            expansionMap.set(key, group.children);
        }
        return { expansions: expansionMap, parentRows: parents };
    }, [props.hospitalProviderTableData, isGrouped, groupBy]);
    /* oxlint-enable sort-keys */

    // Grouped views paginate the parent groups client-side; the flat view is server-paginated.
    const pagedParents = useMemo(
        () => parentRows.slice((props.pageIndex - 1) * props.pageSize, props.pageIndex * props.pageSize),
        [parentRows, props.pageIndex, props.pageSize]
    );
    const pagedExpansions = useMemo(() => {
        const map = new Map<string, ProviderHubContractEntityProvidersTableData[]>();
        for (const parent of pagedParents) map.set(parent[wafflesKeyColumnName], expansions.get(parent[wafflesKeyColumnName]) ?? []);
        return map;
    }, [pagedParents, expansions]);
    const totalRecords = isGrouped ? parentRows.length : props.hospitalProviderTableData.totalCount;
    const paginationProps: WafflesPaginationProps = {
        changePage: (pageNumber: number) => handlePageChange(pageNumber),
        // Flat view pages on the server; grouped views page the parent groups client-side.
        enableBackendPagination: !isGrouped,
        handlePageSizeChange: (pageSize: number) => handlePageSizeChange(pageSize),
        pageSizeOptions: [50, 100, 150],
        recordsPerPage: props.pageSize,
        resetToFirstPage: props.resetToFirstPage,
        showPageSizeSelector: true,
        skip: isGrouped ? (props.pageIndex - 1) * props.pageSize : undefined,
        totalRecords
    }

    return (
        <PageSection>
            <>
                <Waffles<ProviderHubContractEntityProvidersTableData>
                    columns={providerHubProvidersTableColumnConfig}
                    data={isGrouped ? pagedParents : flatRows}
                    dataExpansions={isGrouped ? pagedExpansions : undefined}
                    dataExpansionsDefaultOpen={false}
                    totalNumberOfRecords={totalRecords}
                    enablePagination={true}
                    emptyTableMessage="No matching provider results found. Please start a new search"
                    fullWidth={true}
                    paginationProps={paginationProps}
                    testId="provider-hub__hospital-providers-table"
                    theme={WafflesThemes.ContractEntityDetails}
                    disableStickyHeader={true}
                    customSort={isGrouped ? undefined : handleSortChange}
                    passThroughSorting={!isGrouped}
                    sortedColumnOverride={sortedColumn}
                    sortDirectionOverride={sortDirection}
                    variant="frozen-border"
                />
            </>
        </PageSection>
    )
}

export default HospitalProfileTable;
